import { gql, useMutation, useQuery } from '@apollo/client'

/** A delegated action request (matches the GraphQL ActionRequest type). */
export interface ActionRequest {
  id: string
  requestedByName?: string | null
  targetRole: string
  actionType: string
  title: string
  message?: string | null
  context?: string | null
  status: string
  createdAt?: string | null
}

export const MY_ACTION_REQUESTS = gql`
  query MyActionRequests {
    myActionRequests {
      id
      requestedByName
      targetRole
      actionType
      title
      message
      context
      status
      createdAt
    }
  }
`

const RAISE_ACTION_REQUEST = gql`
  mutation RaiseActionRequest($input: RaiseActionRequestInput!) {
    raiseActionRequest(input: $input) {
      id
      status
    }
  }
`

const RESOLVE_ACTION_REQUEST = gql`
  mutation ResolveActionRequest($id: ID!) {
    resolveActionRequest(id: $id) {
      id
      status
    }
  }
`

const DISMISS_ACTION_REQUEST = gql`
  mutation DismissActionRequest($id: ID!) {
    dismissActionRequest(id: $id) {
      id
      status
    }
  }
`

export interface RaiseActionRequestInput {
  actionType: string
  targetRole: string
  title: string
  message?: string | null
  context?: string | null
}

/** Shared role inbox for the topbar bell — delegated requests + fulfil/dismiss. */
export function useActionInbox() {
  const { data, refetch, loading } = useQuery(MY_ACTION_REQUESTS, {
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all',
    pollInterval: 60000,
  })
  const [resolveMutation] = useMutation(RESOLVE_ACTION_REQUEST)
  const [dismissMutation] = useMutation(DISMISS_ACTION_REQUEST)

  const inbox: ActionRequest[] = data?.myActionRequests ?? []

  const resolve = async (id: string) => {
    await resolveMutation({ variables: { id } })
    await refetch()
  }
  const dismiss = async (id: string) => {
    await dismissMutation({ variables: { id } })
    await refetch()
  }

  return { inbox, count: inbox.length, loading, resolve, dismiss, refetch }
}

/** Raise a delegated request. Used by the reusable <RequestAccess> button. */
export function useRaiseActionRequest() {
  const [raiseMutation, { loading }] = useMutation(RAISE_ACTION_REQUEST, {
    refetchQueries: [{ query: MY_ACTION_REQUESTS }],
  })
  const raise = (input: RaiseActionRequestInput) => raiseMutation({ variables: { input } })
  return { raise, loading }
}
